import React, { useState, useEffect } from "react";
import Alert from '@material-ui/lab/Alert';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  Divider,
  InputAdornment
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  searchSection: {
    marginBottom: theme.spacing(2)
  },
  customerList: {
    maxHeight: 200,
    overflow: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1)
  },
  customerItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    },
    "&.selected": {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText
    }
  },
  formSection: {
    marginTop: theme.spacing(2)
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600
  },
  duplicateWarning: {
    marginTop: theme.spacing(2)
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(0.5)
  },
  identifiedBadge: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText
  }
}));

const formatCPF = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatCNPJ = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatCEP = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const CustomerIdentificationModal = ({
  open,
  onClose,
  contact,
  contactId: contactIdProp, // Fallback if contact.id is not available
  onIdentified
}) => {
  // Use contact.id if available, otherwise use contactIdProp
  const effectiveContactId = contact?.id || contactIdProp;
  const classes = useStyles();

  // Debug logging on mount
  useEffect(() => {
    if (open) {
      console.log("[CustomerIdentificationModal] Props received:", {
        contact,
        contactId: contactIdProp,
        effectiveContactId,
        contactHasId: !!contact?.id
      });
    }
  }, [open, contact, contactIdProp, effectiveContactId]);

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Form fields
  const [personType, setPersonType] = useState("PF");
  const [cpf, setCpf] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [street, setStreet] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [addressReference, setAddressReference] = useState("");

  // Auto-search when modal opens with contact phone and pre-fill form
  useEffect(() => {
    if (open && contact) {
      // Search by phone to find matches
      if (contact.number) {
        searchByPhone(contact.number);
      }

      // Pre-fill form with contact data (always set values, even if empty/null)
      setPersonType(contact.personType || "PF");
      setCpf(contact.cpf ? formatCPF(contact.cpf) : "");
      setCnpj(contact.cnpj ? formatCNPJ(contact.cnpj) : "");
      setCompanyName(contact.companyName || "");
      setTradeName(contact.tradeName || "");
      setZipCode(contact.zipCode ? formatCEP(contact.zipCode) : "");
      setStreet(contact.street || "");
      setStreetNumber(contact.streetNumber || "");
      setComplement(contact.complement || "");
      setNeighborhood(contact.neighborhood || "");
      setCity(contact.city || "");
      setState(contact.state || "");
      setAddressReference(contact.addressReference || "");
    }
  }, [open, contact]);

  const searchByPhone = async (phone) => {
    if (!phone) return;

    setSearching(true);
    try {
      const { data } = await api.get("/customers/find-by-phone", {
        params: { phone }
      });

      if (data.found && data.customers.length > 0) {
        setSearchResults(data.customers);

        // If only one match, auto-select
        if (data.customers.length === 1) {
          handleSelectCustomer(data.customers[0]);
        }
      }
    } catch (error) {
      console.error("Error searching by phone:", error);
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) return;

    setSearching(true);
    try {
      const { data } = await api.get("/customers/search", {
        params: { query: searchQuery }
      });

      setSearchResults(data.customers || []);
    } catch (error) {
      console.error("Error searching customers:", error);
      toast.error("Erro ao buscar clientes");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);

    // Pre-fill form with selected customer data
    setPersonType(customer.personType || "PF");
    if (customer.cpf) setCpf(formatCPF(customer.cpf));
    if (customer.cnpj) setCnpj(formatCNPJ(customer.cnpj));
    if (customer.companyName) setCompanyName(customer.companyName);
  };

  const handleDocumentChange = async (value, type) => {
    const formatted = type === "cpf" ? formatCPF(value) : formatCNPJ(value);

    if (type === "cpf") {
      setCpf(formatted);
    } else {
      setCnpj(formatted);
    }

    // Check for duplicates when document is complete
    const digits = value.replace(/\D/g, "");
    if ((type === "cpf" && digits.length === 11) || (type === "cnpj" && digits.length === 14)) {
      try {
        const { data } = await api.get("/customers/find-by-document", {
          params: { document: digits }
        });

        if (data.found && data.customer && data.customer.id !== contact?.id) {
          setDuplicateWarning({
            exists: true,
            customer: data.customer,
            message: `Este ${type.toUpperCase()} já está cadastrado para: ${data.customer.name}`
          });
        } else {
          setDuplicateWarning(null);
        }
      } catch (error) {
        console.error("Error checking document:", error);
      }
    }
  };

  const handleCEPSearch = async (cep) => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await response.json();

      if (!data.erro) {
        setStreet(data.logradouro || "");
        setNeighborhood(data.bairro || "");
        setCity(data.localidade || "");
        setState(data.uf || "");
      }
    } catch (error) {
      console.error("Error fetching CEP:", error);
    }
  };

  const handleSave = async () => {
    console.log("[CustomerIdentificationModal] handleSave called with:", {
      effectiveContactId,
      contact,
      contactIdProp
    });

    if (!effectiveContactId) {
      const errorMsg = `Contato não carregado (ID: ${contact?.id || 'null'}, Prop: ${contactIdProp || 'null'}). Feche e reabra o modal.`;
      toast.error(errorMsg);
      console.error("CustomerIdentificationModal: No contact ID available.", { contact, contactIdProp, effectiveContactId });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        personType,
        cpf: personType === "PF" ? cpf.replace(/\D/g, "") : undefined,
        cnpj: personType === "PJ" ? cnpj.replace(/\D/g, "") : undefined,
        companyName: personType === "PJ" ? companyName : undefined,
        tradeName: personType === "PJ" ? tradeName : undefined,
        zipCode: zipCode.replace(/\D/g, ""),
        street,
        streetNumber,
        complement,
        neighborhood,
        city,
        state,
        addressReference
      };

      const { data } = await api.put(`/customers/${effectiveContactId}/identify`, payload);

      if (data.duplicateWarning?.exists) {
        toast.warning(data.duplicateWarning.message);
      } else {
        toast.success("Cliente identificado com sucesso!");
      }

      if (onIdentified) {
        onIdentified(data.contact);
      }

      handleClose();
    } catch (error) {
      console.error("Error identifying customer:", error);
      const errorMessage = error.response?.data?.error || "Erro ao identificar cliente";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setSearchQuery("");
    setSearchResults([]);
    setSelectedCustomer(null);
    setDuplicateWarning(null);
    setPersonType("PF");
    setCpf("");
    setCnpj("");
    setCompanyName("");
    setTradeName("");
    setZipCode("");
    setStreet("");
    setStreetNumber("");
    setComplement("");
    setNeighborhood("");
    setCity("");
    setState("");
    setAddressReference("");

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box className={classes.dialogTitle}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            Identificar Cliente
          </Typography>
          {contact?.name && (
            <Chip
              label={contact.name}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search Section */}
        <Box className={classes.searchSection}>
          <Typography variant="subtitle2" color="textSecondary">
            Buscar cliente existente
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Buscar por nome, telefone, CPF ou CNPJ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} disabled={searching}>
                    {searching ? <CircularProgress size={20} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {searchResults.length > 0 && (
            <List className={classes.customerList}>
              {searchResults.map((customer) => (
                <ListItem
                  key={customer.id}
                  className={`${classes.customerItem} ${selectedCustomer?.id === customer.id ? "selected" : ""}`}
                  onClick={() => handleSelectCustomer(customer)}
                  button
                >
                  <ListItemText
                    primary={customer.name}
                    secondary={
                      <Box className={classes.chipContainer}>
                        {customer.cpf && (
                          <Chip size="small" label={`CPF: ${formatCPF(customer.cpf)}`} />
                        )}
                        {customer.cnpj && (
                          <Chip size="small" label={`CNPJ: ${formatCNPJ(customer.cnpj)}`} />
                        )}
                        {customer.city && (
                          <Chip size="small" label={`${customer.city}/${customer.state}`} variant="outlined" />
                        )}
                        {customer.identifiedAt && (
                          <Chip size="small" label="Identificado" className={classes.identifiedBadge} />
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    {selectedCustomer?.id === customer.id && (
                      <CheckIcon color="primary" />
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Divider />

        {/* Person Type Selection */}
        <Box className={classes.formSection}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Tipo de Pessoa</FormLabel>
            <RadioGroup
              row
              value={personType}
              onChange={(e) => setPersonType(e.target.value)}
            >
              <FormControlLabel
                value="PF"
                control={<Radio color="primary" />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon /> Pessoa Fisica
                  </Box>
                }
              />
              <FormControlLabel
                value="PJ"
                control={<Radio color="primary" />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon /> Pessoa Juridica
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Document Fields */}
        <Box className={classes.formSection}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Documento
          </Typography>
          <Grid container spacing={2}>
            {personType === "PF" ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CPF"
                  variant="outlined"
                  size="small"
                  value={cpf}
                  onChange={(e) => handleDocumentChange(e.target.value, "cpf")}
                  placeholder="000.000.000-00"
                  inputProps={{ maxLength: 14 }}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="CNPJ"
                    variant="outlined"
                    size="small"
                    value={cnpj}
                    onChange={(e) => handleDocumentChange(e.target.value, "cnpj")}
                    placeholder="00.000.000/0000-00"
                    inputProps={{ maxLength: 18 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Razao Social"
                    variant="outlined"
                    size="small"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome Fantasia"
                    variant="outlined"
                    size="small"
                    value={tradeName}
                    onChange={(e) => setTradeName(e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {duplicateWarning?.exists && (
            <Alert
              severity="warning"
              className={classes.duplicateWarning}
              icon={<WarningIcon />}
            >
              {duplicateWarning.message}
            </Alert>
          )}
        </Box>

        {/* Address Fields */}
        <Box className={classes.formSection}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Endereco de Entrega
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CEP"
                variant="outlined"
                size="small"
                value={zipCode}
                onChange={(e) => {
                  const formatted = formatCEP(e.target.value);
                  setZipCode(formatted);
                  if (formatted.replace(/\D/g, "").length === 8) {
                    handleCEPSearch(formatted);
                  }
                }}
                placeholder="00000-000"
                inputProps={{ maxLength: 9 }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Rua"
                variant="outlined"
                size="small"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Numero"
                variant="outlined"
                size="small"
                value={streetNumber}
                onChange={(e) => setStreetNumber(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Complemento"
                variant="outlined"
                size="small"
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bairro"
                variant="outlined"
                size="small"
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Cidade"
                variant="outlined"
                size="small"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="UF"
                variant="outlined"
                size="small"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Referencia"
                variant="outlined"
                size="small"
                value={addressReference}
                onChange={(e) => setAddressReference(e.target.value)}
                placeholder="Ex: Proximo ao mercado..."
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          Salvar Identificacao
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerIdentificationModal;
